import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomSearchBar from "../Lease Module/CustomSearchBar";
import BankAuctionReusableCard from "./BankAuctionReusableCard";
import CustomBottomNav from "../Rent Module/BottomNavbar/BottomNavbar";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon issue
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// BANK AUCTION API
const BANK_API = "https://landnest.net:81/auction-property/";

// Property type filter options
const rentalTypes = [
  "PLOT/LAND", "COMMERCIAL LAND/PLOT", "RENT WITH DUPLEX BUILDING", "DUPLEX HOUSE",
  "RENTAL BUILDING", "PG-OFFICES", "FLAT", "VILLA",
  "COMMERCIAL BUILDING", "APARTMENT", "JV/JD"
];

const PostBankMap = () => {
  const navigate = useNavigate();

  // --- STATE ---
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const [activePropertyId, setActivePropertyId] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  
  // State for carousel arrows
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const mapContainerRef = useRef(null);
  const cardListRef = useRef(null);

  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [currentBounds, setCurrentBounds] = useState(null);
  const [mapKey, setMapKey] = useState(0);

  // Ref for skipping map pan
  const skipMapPanRef = useRef(false);

  const fallbackCoordinates = [
    { lat: 17.3850, lng: 78.4867 },
    { lat: 17.4500, lng: 78.3800 },
    { lat: 17.3000, lng: 78.5500 },
    { lat: 17.4100, lng: 78.4000 },
  ];

  // ----------------------------------------------------------------
  // FETCH BANK AUCTION PROPERTIES
  // ----------------------------------------------------------------
  useEffect(() => {
    const fetchBankAuctions = async () => {
      try {
        const res = await axios.get(BANK_API);

        const parsed = res.data.map((item, index) => ({
          id: String(item.bankprop_id),
          title: item.bank_name || "Bank Property",
          property_type: item.property_type,
          location: item.city_town || item.location || "Unknown",
          price: item.reserve_price || 0,
          possession: item.possession,
          createdAt: item.created_at,
          fullData: item,
          lat: fallbackCoordinates[index % 4].lat,
          lng: fallbackCoordinates[index % 4].lng,
          images: [],
          bank_pro_doc: item.bank_pro_doc || [] // Add this for images
        }));

        setProperties(parsed);
        setFilteredProperties(parsed);
      } catch (err) {
        console.error("Error fetching bank auction properties:", err);
      }
    };

    fetchBankAuctions();
  }, []);

  // ----------------------------------------------------------------
  // FILTER PROPERTIES BASED ON SELECTED PROPERTY TYPE
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!selectedPropertyType) {
      setFilteredProperties(properties);
      return;
    }

    const filtered = properties.filter(property => 
      property.property_type?.toUpperCase() === selectedPropertyType.toUpperCase()
    );
    
    setFilteredProperties(filtered);
  }, [selectedPropertyType, properties]);

  // ----------------------------------------------------------------
  // SORT BY DISTANCE BASED ON SEARCH LOCATION
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!searchLocation) return;

    const sorted = filteredProperties
      .map((p) => ({
        ...p,
        distance: L.latLng(p.lat, p.lng).distanceTo([
          searchLocation.lat,
          searchLocation.lng,
        ]),
      }))
      .sort((a, b) => a.distance - b.distance);

    setFilteredProperties(sorted);
  }, [searchLocation]);

  // ----------------------------------------------------------------
  // INITIALIZE MAP
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!mapContainerRef.current) return;

    let leafletMap = L.map(mapContainerRef.current, {
      center: searchLocation ? [searchLocation.lat, searchLocation.lng] : [17.385, 78.486],
      zoom: 11,
      zoomControl: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
      leafletMap
    );

    L.control.zoom({ position: "topright" }).addTo(leafletMap);

    leafletMap.on("moveend", () => {
      setCurrentBounds(leafletMap.getBounds());
    });

    setMap(leafletMap);
    setMapLoaded(true);
    setCurrentBounds(leafletMap.getBounds());

    return () => leafletMap.remove();
  }, [mapKey]);

  // ----------------------------------------------------------------
  // UPDATE MAP WHEN SEARCH LOCATION CHANGES
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!map || !searchLocation) return;

    map.panTo([searchLocation.lat, searchLocation.lng], {
      animate: true,
      duration: 1,
    });
    map.setZoom(13);
  }, [searchLocation, map]);

  // ----------------------------------------------------------------
  // FILTER PROPERTIES BASED ON MAP BOUNDS
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!currentBounds || filteredProperties.length === 0) {
      setVisibleProperties(filteredProperties);
      return;
    }

    const inBounds = filteredProperties.filter((p) =>
      currentBounds.contains(L.latLng(p.lat, p.lng))
    );

    setVisibleProperties(inBounds);
  }, [currentBounds, filteredProperties]);

  // ----------------------------------------------------------------
  // CREATE MARKERS
  // ----------------------------------------------------------------
  const createMarkerIcon = (active = false) =>
    L.divIcon({
      className: "custom-marker",
      html: `
        <div style="
          width:${active ? 26 : 20}px;
          height:${active ? 26 : 20}px;
          background:#008000;
          border-radius:50%;
          border:2px solid ${active ? "red" : "white"};
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        "></div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

  useEffect(() => {
    if (!mapLoaded || !map) return;

    markers.forEach((m) => map.removeLayer(m));

    const newMarkers = [];

    filteredProperties.forEach((property) => {
      const marker = L.marker([property.lat, property.lng], {
        icon: createMarkerIcon(property.id === activePropertyId),
        propertyId: property.id,
      }).addTo(map);

      // FIXED: Add click handler to navigate to property details in new tab
      marker.on("click", (e) => {
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
        
        skipMapPanRef.current = true;
        setActivePropertyId(property.id);
        
        // Scroll to card
        setTimeout(() => {
          scrollToCard(property.id);
          
          // Open in new tab after a short delay (like Buy_Property_Map)
          setTimeout(() => {
            window.open(`/Post_Bankcard_indetail/${property.id}`, '_blank');
            skipMapPanRef.current = false;
          }, 500);
        }, 50);
      });

      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
  }, [mapLoaded, map, filteredProperties, activePropertyId]);

  // ----------------------------------------------------------------
  // UPDATE MARKER WHEN ACTIVE PROPERTY CHANGES
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!map || !markers.length) return;

    markers.forEach(m => {
      try {
        const pid = m.options.propertyId;
        const prop = filteredProperties.find(p => p.id === pid);
        if (prop) {
          m.setIcon(createMarkerIcon(pid === activePropertyId));
        }
      } catch (err) { console.error(err); }
    });

    // Pan to active property if not skipping
    if (!skipMapPanRef.current && map) {
      try {
        const activeProp = filteredProperties.find(p => p.id === activePropertyId);
        if (activeProp && activeProp.lat && activeProp.lng) {
          setTimeout(() => {
            if (map && map.panTo) {
              map.panTo([activeProp.lat, activeProp.lng], { 
                animate: true, 
                duration: 0.4 
              });
            }
          }, 50);
        }
      } catch (error) {
        console.warn('Could not pan to property:', error);
      }
    }
  }, [activePropertyId, markers, map, filteredProperties]);

  // ----------------------------------------------------------------
  // SCROLL TO CARD ON MARKER CLICK
  // ----------------------------------------------------------------
  const scrollToCard = (propertyId) => {
    try {
      const container = cardListRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-property-id="${propertyId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    } catch (err) { console.error('scrollToCard error', err); }
  };

  // ----------------------------------------------------------------
  // CHECK CAROUSEL SCROLL POSITION
  // ----------------------------------------------------------------
  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const checkScrollPosition = () => {
      if (!container || visibleProperties.length === 0) return;
      
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      
      setIsAtStart(scrollLeft <= 10);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 10);
    };

    const handleScroll = () => {
      checkScrollPosition();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    checkScrollPosition();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [visibleProperties]);

  // ----------------------------------------------------------------
  // HANDLE CAROUSEL ARROW CLICKS (Infinite Scroll)
  // ----------------------------------------------------------------
  const handleLeftArrowClick = () => {
    const container = cardListRef.current;
    if (!container || visibleProperties.length === 0) return;

    if (isAtStart) {
      // If at start, scroll to the end
      container.scrollTo({
        left: container.scrollWidth,
        behavior: 'smooth'
      });
    } else {
      // Normal scroll left
      container.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const handleRightArrowClick = () => {
    const container = cardListRef.current;
    if (!container || visibleProperties.length === 0) return;

    if (isAtEnd) {
      // If at end, scroll to the start
      container.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
    } else {
      // Normal scroll right
      container.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  // ----------------------------------------------------------------
  // HANDLE CARD CLICK (Open in new tab like Buy_Property_Map)
  // ----------------------------------------------------------------
  const handleCardClick = (propertyId) => {
    window.open(`/Post_Bankcard_indetail/${propertyId}`, '_blank');
  };

  // ----------------------------------------------------------------
  // HANDLE PROPERTY TYPE FILTER CLICK
  // ----------------------------------------------------------------
  const handlePropertyTypeClick = (type) => {
    if (selectedPropertyType === type) {
      setSelectedPropertyType(null);
    } else {
      setSelectedPropertyType(type);
    }
  };

  // ----------------------------------------------------------------
  // CLEAR SEARCH
  // ----------------------------------------------------------------
  const clearSearch = () => {
    setSearchLocation(null);
    setSelectedPropertyType(null);
    setMapKey((prev) => prev + 1);
    
    // Reset carousel scroll
    if (cardListRef.current) {
      cardListRef.current.scrollLeft = 0;
    }
    
    setActivePropertyId(null);
  };

  // ----------------------------------------------------------------
  // HANDLE SELECTED LOCATION FROM SEARCH BAR
  // ----------------------------------------------------------------
  const handleSelectedLocation = (locationData) => {
    setSearchLocation(locationData);
    
    setTimeout(() => {
      if (map && locationData && map.panTo) {
        try {
          map.panTo([locationData.lat, locationData.lng], { 
            animate: true, 
            duration: 1 
          });
          map.setZoom(13); 
        } catch (error) {
          console.error('Error panning to searched location:', error);
        }
      }
    }, 150);
  };

  return (
    <Box
      sx={{
        pb: 7,
        maxWidth: 480,
        mx: "auto",
        position: "relative",
        backgroundColor: "rgb(239, 231, 221)",
        minHeight: 500,
      }}
    >
      {/* Top Section with Search Bar - Fixed 70px height */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          bgcolor: "rgb(239, 231, 221)",
          px: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Search Bar */}
        <Box sx={{ width: "100%" }}>
          <CustomSearchBar
            onPlaceSelected={handleSelectedLocation}
            onClear={clearSearch}
            hidePriceFilter={true}
          />
        </Box>
      </Box>

      {/* Title and Filters Section */}
      <Box sx={{ px: 2, pt: 1 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
          Bank Auction Properties
        </Typography>
      </Box>

      {/* Property Type Filter Chips - Horizontal Scrollable */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            pb: 1,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {rentalTypes.map((type, index) => (
            <Chip
              key={index}
              label={type}
              variant="filled"
              onClick={() => {
                setSelectedPropertyType(prev => (prev === type ? null : type));
              }}
              sx={{ 
                flexShrink: 0,
                bgcolor: selectedPropertyType === type ? '#000000' : 'transparent',
                color: selectedPropertyType === type ? '#ffffff' : '#000000',
                border: '1px solid black',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: selectedPropertyType === type ? '#000000' : 'rgba(0, 0, 0, 0.1)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Map Container */}
      <Box sx={{ px: 2, pb: 10, position: "relative" }}>
        <div
          ref={mapContainerRef}
          key={`map-${mapKey}`}
          style={{
            width: "100vw",
            height: "calc(100vh - 305px)", // Adjusted for 70px top + title + chips
            marginLeft: "-16px",
            marginRight: "-16px",
            borderRadius: "6px",
            overflow: "hidden",
            background: "#eaeaea",
          }}
        />

        {/* Property Cards Carousel */}
        {visibleProperties.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -8,
              zIndex: 1001,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              pointerEvents: 'auto',
            }}
          >
            {/* Left Arrow */}
            <Button
              onClick={handleLeftArrowClick}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                p: 1,
                left: 5,
                borderRadius: '70%',
                bgcolor: 'white',
                boxShadow: 2,
                mr: 1,
                '&:hover': { bgcolor: 'grey.200' },
                zIndex: 1002,
              }}
            >
              &lt;
            </Button>

            <Box
              ref={cardListRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 2,
                flexGrow: 1,
                scrollSnapType: "x mandatory",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {visibleProperties.map((prop) => (
                <Box
                  key={prop.id}
                  data-property-id={prop.id}
                  sx={{
                    flex: "0 0 280px",
                    scrollSnapAlign: "center",
                    cursor: "pointer",
                    height: "140px", // Reduced to match card height
                  }}
                  onClick={() => handleCardClick(prop.id)}
                >
                  <div style={{ padding: 6, height: '100%' }}>
                    <BankAuctionReusableCard
                      property={prop}
                      onClick={() => handleCardClick(prop.id)}
                    />
                  </div>
                </Box>
              ))}
            </Box>

            {/* Right Arrow */}
            <Button
              onClick={handleRightArrowClick}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'white',
                boxShadow: 2,
                p: 1,
                right: 5,
                ml: 1,
                '&:hover': { bgcolor: 'grey.200' },
                zIndex: 1002,
              }}
            >
              &gt;
            </Button>
          </Box>
        )}

        {/* No Properties Message */}
        {visibleProperties.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              background: "white",
              padding: "6px 16px",
              borderRadius: "6px",
              boxShadow: 3,
              zIndex: 1001,
            }}
          >
            <Typography>
              {selectedPropertyType 
                ? `No ${selectedPropertyType} properties found` 
                : "No properties found in this area"
              }
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bottom Navigation */}
      <Paper
        sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}
        elevation={3}
      >
        <CustomBottomNav />
      </Paper>
    </Box>
  );
};

export default PostBankMap;